import React, { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  Crosshair,
  X,
  Check,
  FileText,
} from "lucide-react";
import clsx from "clsx";
import { usePdfRenderer } from "hooks/usePdfRenderer";
import { pdfFileUrl } from "../../utils/fetchResearchReport";

interface CropModalProps {
  projectId: string;
  filename: string;
  page: number;
  maxPage: number;
  onCapture: (base64: string) => void;
  onClose: () => void;
  onPageChange: (page: number) => void;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const CropModal = ({
  projectId,
  filename,
  page,
  maxPage,
  onCapture,
  onClose,
  onPageChange,
}: CropModalProps) => {
  const { t } = useTranslation();

  // State
  const [rendered, setRendered] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [startPt, setStartPt] = useState({ x: 0, y: 0 });
  const [rect, setRect] = useState<Rect | null>(null);

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { renderPage } = usePdfRenderer();

  // Render PDF page to canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setRendered(false);
    setRect(null);

    const containerWidth = containerRef.current?.clientWidth || 800;

    const renderPdfPage = async () => {
      const url = await pdfFileUrl(projectId, filename);
      const result = await renderPage(canvas, url, page, {
        containerWidth,
        maxCanvasSide: 4096,
      });
      if (result) setRendered(true);
    };

    renderPdfPage();
  }, [page, filename, projectId, renderPage]);

  const getMousePos = useCallback(
    (e: React.MouseEvent): { x: number; y: number } => {
      const overlay = overlayRef.current;
      if (!overlay) return { x: 0, y: 0 };
      const bounds = overlay.getBoundingClientRect();
      return { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const pos = getMousePos(e);
      setStartPt(pos);
      setRect(null);
      setDrawing(true);
    },
    [getMousePos],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!drawing) return;
      const pos = getMousePos(e);
      setRect({
        x: Math.min(startPt.x, pos.x),
        y: Math.min(startPt.y, pos.y),
        w: Math.abs(pos.x - startPt.x),
        h: Math.abs(pos.y - startPt.y),
      });
    },
    [drawing, startPt, getMousePos],
  );

  const handleMouseUp = useCallback(() => {
    setDrawing(false);
  }, []);

  const handleCapture = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !rect || rect.w < 5 || rect.h < 5) return;

    const cssW = canvas.clientWidth;
    const scaleX = canvas.width / cssW;
    const cssH = canvas.clientHeight;
    const scaleY = canvas.height / cssH;

    const offscreen = document.createElement("canvas");
    const cropW = Math.round(rect.w * scaleX);
    const cropH = Math.round(rect.h * scaleY);
    offscreen.width = cropW;
    offscreen.height = cropH;

    const ctx = offscreen.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      canvas,
      Math.round(rect.x * scaleX),
      Math.round(rect.y * scaleY),
      cropW,
      cropH,
      0,
      0,
      cropW,
      cropH,
    );

    const dataUrl = offscreen.toDataURL("image/png");
    const base64 = dataUrl.replace("data:image/png;base64,", "");
    onCapture(base64);
  }, [rect, onCapture]);

  const canCapture = rect && rect.w >= 5 && rect.h >= 5;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl flex flex-col w-[90vw] max-w-5xl h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="flex items-center gap-2">
            <Crosshair size={16} className="text-blue-600" />
            <span className="text-sm font-semibold text-gray-700">
              {t(
                "smartSurveyor.researchReport.cropFrom",
                "Crop from: {{filename}} p.{{page}}",
                { filename, page },
              )}
            </span>
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-default"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-gray-600 font-medium min-w-[60px] text-center">
              {t(
                "smartSurveyor.researchReport.pageOf",
                "Page {{page}} / {{max}}",
                {
                  page,
                  max: maxPage,
                },
              )}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(maxPage, page + 1))}
              disabled={page >= maxPage}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-default"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCapture}
              disabled={!canCapture}
              className={clsx(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                canCapture
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed",
              )}
            >
              <Check size={14} />
              {t("smartSurveyor.researchReport.capture", "Capture")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-gray-600 border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              <X size={14} />
              {t("smartSurveyor.researchReport.cancel", "Cancel")}
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-gray-100 flex items-start justify-center p-4 relative"
        >
          {!rendered && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-100 z-10">
              <div className="relative">
                <div className="w-16 h-20 bg-white rounded-lg shadow-sm border border-gray-200 flex items-center justify-center">
                  <FileText size={28} className="text-gray-300" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              </div>
              <span className="text-xs text-gray-400 font-medium">
                Loading page...
              </span>
            </div>
          )}
          <div className="relative inline-block">
            <canvas ref={canvasRef} className="shadow-md select-none" />
            {rendered && (
              <div
                ref={overlayRef}
                className="absolute inset-0 cursor-crosshair"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {rect && rect.w > 2 && rect.h > 2 && (
                  <div
                    className="absolute border-2 border-dashed border-blue-500 bg-blue-500/10 pointer-events-none"
                    style={{
                      left: rect.x,
                      top: rect.y,
                      width: rect.w,
                      height: rect.h,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
